import { AdConfig, AdSlotConfig, BidderConfig } from '../types/ads';

/**
 * Bidder configurations for Prebid.js header bidding.
 *
 * Each bidder competes in a unified auction before the ad request goes to
 * Google Ad Manager (GAM). GAM then runs its own auction (including Google
 * AdX / AdSense demand and any Open Bidding partners) against the Prebid
 * winning bid. The highest overall CPM wins the impression.
 *
 * Bidder adapter docs: https://docs.prebid.org/dev-docs/bidders.html
 *
 * To activate a bidder, set the corresponding REACT_APP_* env vars with
 * your real account/placement IDs. The defaults below are Prebid community
 * test IDs that return test creatives — safe for development.
 */
export const BIDDER_CONFIGS: Record<string, BidderConfig> = {
  // ── Xandr (formerly AppNexus) ──────────────────────────────────────
  appnexus: {
    bidder: 'appnexus',
    params: {
      placementId: process.env.REACT_APP_APPNEXUS_PLACEMENT_ID || '13144370'
    }
  },

  // ── Magnite (formerly Rubicon Project) ─────────────────────────────
  rubicon: {
    bidder: 'rubicon',
    params: {
      accountId: process.env.REACT_APP_RUBICON_ACCOUNT_ID || '17282',
      siteId: process.env.REACT_APP_RUBICON_SITE_ID || '162036',
      zoneId: process.env.REACT_APP_RUBICON_ZONE_ID || '776750'
    }
  },

  // ── OpenX ──────────────────────────────────────────────────────────
  openx: {
    bidder: 'openx',
    params: {
      unit: process.env.REACT_APP_OPENX_UNIT_ID || '539439964',
      delDomain: process.env.REACT_APP_OPENX_DOMAIN || 'se-demo-d.openx.net'
    }
  },

  // ── Amazon TAM (Transparent Ad Marketplace) ────────────────────────
  // Loaded via a separate apstag.js script; the Prebid adapter bridges
  // Amazon demand into the unified auction.
  amazon: {
    bidder: 'amazon',
    params: {
      pubID: process.env.REACT_APP_AMAZON_PUB_ID || 'fashion-rating-app',
      adSlot: 'fashion-rating-default'
    }
  },

  // ── Index Exchange ─────────────────────────────────────────────────
  ix: {
    bidder: 'ix',
    params: {
      siteId: process.env.REACT_APP_IX_SITE_ID || '197232',
      size: [300, 250]
    }
  },

  // ── PubMatic (OpenWrap) ────────────────────────────────────────────
  pubmatic: {
    bidder: 'pubmatic',
    params: {
      publisherId: process.env.REACT_APP_PUBMATIC_PUBLISHER_ID || '156209',
      adSlot: process.env.REACT_APP_PUBMATIC_AD_SLOT || 'fashion_rating_app_300x250'
    }
  },

  // ── Sovrn (formerly Lijit) ─────────────────────────────────────────
  sovrn: {
    bidder: 'sovrn',
    params: {
      tagid: process.env.REACT_APP_SOVRN_TAG_ID || '403370'
    }
  },

  // ── Criteo ─────────────────────────────────────────────────────────
  criteo: {
    bidder: 'criteo',
    params: {
      networkId: parseInt(process.env.REACT_APP_CRITEO_NETWORK_ID || '7100', 10)
    }
  },

  // ── TripleLift (native + display) ──────────────────────────────────
  triplelift: {
    bidder: 'triplelift',
    params: {
      inventoryCode: process.env.REACT_APP_TRIPLELIFT_INVENTORY_CODE || 'fashion_rating_app_prebid'
    }
  },

  // ── 33Across ───────────────────────────────────────────────────────
  '33across': {
    bidder: '33across',
    params: {
      siteId: process.env.REACT_APP_33ACROSS_SITE_ID || 'cxBE0qjUir6iopaKkGJozW',
      productId: 'siab'
    }
  },

  // ── ShareThrough ───────────────────────────────────────────────────
  sharethrough: {
    bidder: 'sharethrough',
    params: {
      pkey: process.env.REACT_APP_SHARETHROUGH_PKEY || 'PoAEzHVmgcuHZbiUnKftbLgP'
    }
  },

  // ── GumGum ─────────────────────────────────────────────────────────
  gumgum: {
    bidder: 'gumgum',
    params: {
      inSlot: parseInt(process.env.REACT_APP_GUMGUM_SLOT_ID || '19618', 10)
    }
  },

  // ── Media.net (Yahoo/Bing contextual) ──────────────────────────────
  medianet: {
    bidder: 'medianet',
    params: {
      cid: process.env.REACT_APP_MEDIANET_CID || '8CU5ER86K',
      crid: process.env.REACT_APP_MEDIANET_CRID || '451466393'
    }
  },

  // ── Custom Ad Server ───────────────────────────────────────────────
  customadserver: {
    bidder: 'customadserver',
    params: {
      zoneId: 1,  // default, overridden per slot
      floorPrice: 0
    }
  }
};

/**
 * Default ad slot configurations for different placements
 */
export const AD_SLOT_CONFIGS: Record<string, AdSlotConfig> = {
  'hero-banner': {
    slotId: 'hero-banner',
    sizes: [[728, 90], [970, 90]],
    targeting: {
      keywords: ['fashion', 'style', 'trending'],
      categories: ['homepage', 'hero']
    },
    refreshRate: 30,
    lazy: false,
    priority: 1
  },
  
  'sidebar-ad': {
    slotId: 'sidebar-ad',
    sizes: [[300, 250], [300, 600]],
    targeting: {
      keywords: ['fashion', 'products', 'shopping'],
      categories: ['sidebar', 'desktop']
    },
    refreshRate: 45,
    lazy: true,
    priority: 2
  },
  
  'mobile-banner': {
    slotId: 'mobile-banner',
    sizes: [[320, 50], [320, 100]],
    targeting: {
      keywords: ['fashion', 'mobile', 'style'],
      categories: ['mobile', 'banner']
    },
    refreshRate: 60,
    lazy: true,
    priority: 3
  },
  
  'native-in-feed': {
    slotId: 'native-in-feed',
    sizes: [[300, 250]],
    targeting: {
      keywords: ['fashion', 'native', 'feed'],
      categories: ['native', 'in-feed']
    },
    refreshRate: 0, // No auto-refresh for native ads
    lazy: true,
    priority: 2
  },
  
  'outfit-detail-sidebar': {
    slotId: 'outfit-detail-sidebar',
    sizes: [[300, 250], [300, 600]],
    targeting: {
      keywords: ['fashion', 'outfit', 'products'],
      categories: ['outfit-detail', 'sidebar']
    },
    refreshRate: 30,
    lazy: true,
    priority: 1
  },
  
  'search-results-top': {
    slotId: 'search-results-top',
    sizes: [[728, 90], [970, 90]],
    targeting: {
      keywords: ['fashion', 'search', 'results'],
      categories: ['search', 'top']
    },
    refreshRate: 0, // No auto-refresh on search
    lazy: false,
    priority: 1
  },
  
  'profile-sidebar': {
    slotId: 'profile-sidebar',
    sizes: [[300, 250]],
    targeting: {
      keywords: ['fashion', 'profile', 'personal'],
      categories: ['profile', 'sidebar']
    },
    refreshRate: 60,
    lazy: true,
    priority: 3
  },

  'travel-destination-card': {
    slotId: 'travel-destination-card',
    sizes: [[345, 400]],
    targeting: {
      keywords: ['travel', 'vacation', 'destination', 'fashion'],
      categories: ['travel', 'contextual']
    },
    refreshRate: 0, // No auto-refresh for travel cards
    lazy: true,
    priority: 2
  },

  'travel-package-carousel': {
    slotId: 'travel-package-carousel',
    sizes: [[728, 300], [970, 300]],
    targeting: {
      keywords: ['travel', 'packages', 'vacation', 'deals'],
      categories: ['travel', 'carousel']
    },
    refreshRate: 0, // No auto-refresh for carousels
    lazy: true,
    priority: 2
  },

  'seasonal-travel-promotion': {
    slotId: 'seasonal-travel-promotion',
    sizes: [[300, 300], [400, 300]],
    targeting: {
      keywords: ['travel', 'seasonal', 'promotion', 'deals'],
      categories: ['travel', 'seasonal']
    },
    refreshRate: 0, // No auto-refresh for promotions
    lazy: true,
    priority: 1
  },

  'travel-experience-banner': {
    slotId: 'travel-experience-banner',
    sizes: [[728, 200], [970, 200]],
    targeting: {
      keywords: ['travel', 'experience', 'adventure', 'vacation'],
      categories: ['travel', 'banner']
    },
    refreshRate: 0, // No auto-refresh for experience banners
    lazy: true,
    priority: 2
  }
};

/**
 * Complete ad configuration
 */
export const AD_CONFIG: AdConfig = {
  slots: AD_SLOT_CONFIGS,
  
  prebid: {
    timeout: parseInt(process.env.REACT_APP_PREBID_TIMEOUT || '3000'),
    bidders: Object.values(BIDDER_CONFIGS),
    priceGranularity: 'dense',
    enableSendAllBids: true,
    useBidCache: true,
    enableHeaderBidding: true
  },
  
  gam: {
    networkId: process.env.REACT_APP_GAM_NETWORK_ID || 'ca-pub-7791049499700753',
    enableSRA: true,
    collapseEmptyDivs: true,
    enableLazyLoad: true,
    lazyLoadConfig: {
      fetchMarginPercent: 500,
      renderMarginPercent: 200,
      mobileScaling: 2.0
    },
    enableHeaderBidding: true,
    headerBiddingTimeout: 2000
  },
  
  analytics: {
    enabled: process.env.NODE_ENV === 'production',
    providers: ['ga', 'prebid']
  }
};

/**
 * Get ad slot configuration by ID
 */
export function getAdSlotConfig(slotId: string): AdSlotConfig | undefined {
  return AD_SLOT_CONFIGS[slotId];
}

/**
 * Get bidder configuration by name
 */
export function getBidderConfig(bidderName: string): BidderConfig | undefined {
  return BIDDER_CONFIGS[bidderName];
}

/**
 * Get all available ad slot IDs
 */
export function getAvailableSlotIds(): string[] {
  return Object.keys(AD_SLOT_CONFIGS);
}

/**
 * Get bidders for a specific ad slot
 */
export function getBiddersForSlot(slotId: string): BidderConfig[] {
  const slotConfig = getAdSlotConfig(slotId);
  if (!slotConfig) {
    // Ad configuration from backend API call
    // Ad configuration from backend API call
    return [];
  }
  
  // Return all bidders for now, but this could be customized per slot
  return Object.values(BIDDER_CONFIGS);
}

/**
 * Validate ad configuration
 */
export function validateAdConfig(config: Partial<AdConfig>): boolean {
  try {
    // Check required fields
    if (!config.gam?.networkId) {
      console.error('GAM network ID is required');
      return false;
    }
    
    if (!config.prebid?.timeout || config.prebid.timeout <= 0) {
      console.error('Valid Prebid timeout is required');
      return false;
    }
    
    if (!config.prebid?.bidders || config.prebid.bidders.length === 0) {
      console.error('At least one bidder configuration is required');
      return false;
    }
    
    // Validate bidder configurations
    for (const bidder of config.prebid.bidders) {
      if (!bidder.bidder || !bidder.params) {
        console.error(`Invalid bidder configuration: ${JSON.stringify(bidder)}`);
        return false;
      }
    }
    
    // Validate slot configurations
    if (config.slots) {
      for (const [slotId, slotConfig] of Object.entries(config.slots)) {
        if (!slotConfig.sizes || slotConfig.sizes.length === 0) {
          console.error(`Invalid sizes for slot ${slotId}`);
          return false;
        }
        
        if (slotConfig.refreshRate && slotConfig.refreshRate < 0) {
          console.error(`Invalid refresh rate for slot ${slotId}`);
          return false;
        }
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error validating ad configuration:', error);
    return false;
  }
}

/**
 * Get environment-specific configuration
 */
export function getEnvironmentConfig(): Partial<AdConfig> {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isProduction = process.env.NODE_ENV === 'production';
  
  return {
    prebid: {
      ...AD_CONFIG.prebid,
      timeout: process.env.NODE_ENV === "development" ? 5000 : 2000, // Longer timeout in development
    },
    
    gam: {
      ...AD_CONFIG.gam,
      networkId: isProduction 
        ? process.env.REACT_APP_GAM_NETWORK_ID_PROD || AD_CONFIG.gam.networkId
        : process.env.REACT_APP_GAM_NETWORK_ID_DEV || AD_CONFIG.gam.networkId
    },
    
    analytics: {
      enabled: isProduction,
      providers: isProduction ? ['ga', 'prebid'] : ['console']
    }
  };
}

/**
 * Merge configurations with environment overrides
 */
export function getMergedConfig(): AdConfig {
  const envConfig = getEnvironmentConfig();
  
  return {
    ...AD_CONFIG,
    ...envConfig,
    prebid: {
      ...AD_CONFIG.prebid,
      ...envConfig.prebid
    },
    gam: {
      ...AD_CONFIG.gam,
      ...envConfig.gam
    },
    analytics: {
      enabled: AD_CONFIG.analytics?.enabled || false,
      providers: AD_CONFIG.analytics?.providers || [],
      ...envConfig.analytics
    }
  };
}